import React, { useState, useEffect } from "react";
import ReactGA from "react-ga4";
import { initGA, logPageView } from "../../Analytics";
import { sortBy, reverse } from "lodash";
import { connect } from "react-redux";
import * as actions from "../../store/actions/actions";
import * as synopActions from "../../store/actions/synop_actions";
import * as tempActions from "../../store/actions/temp_actions";
import * as marineActions from "../../store/actions/marine_actions";
import * as gcosActions from "../../store/actions/gcos_actions";
import * as gbonActions from "../../store/actions/gbon_actions";

import { Spin } from "antd";
import DBHeader from "./DBHeader/DBHeader";
import DBBody from "./DBBody/DBBody";
import DBFooter from "./DBFooter/DBFooter";
import DBOscarInfo from "./DBOscarInfo/DBOscarInfo";

import {
  getListMonths,
  getListYears,
  processNWPData,
  processGCOSData
} from "./utils";

import "./DatabaseStatus.css";

const DatabaseStatus = props => {
  const module = props.params.module;
  let prefix = "";
  if (props.params.module === "gbon") {
    prefix = "gbon_";
  }
  // the default file types are 'synop'
  // for module 'nwp' and 'gsn' for module 'gcos'
  let [currentFileType, setCurrentFileType] = useState(
    module === "nwp" || module === "gbon" || module === "snow_depth" ? "synop" : "gsn"
  );
  let [tabs, setTabs] = useState({
    nwp: {
      hasHeader: true,
      active: "surface",
      networks: {
        surface: {
          name: "Surface",
          fileType: "synop"
        },
        upperAir: {
          name: "Upper air",
          fileType: "temp"
        }
      }
    },
    marine: {
      hasHeader: true,
      active: "buoy",
      networks: {
        buoy: {
          name: "Buoy",
          fileType: "buoy"
        },
        ship: {
          name: "Ship",
          fileType: "ship"
        }
      }
    },
    gbon: {
      hasHeader: true,
      active: "surface",
      networks: {
        surface: {
          name: "Surface",
          fileType: "synop"
        },
        upperAir: {
          name: "Upper air",
          fileType: "temp"
        }
      }
    },
    gcos: {
      hasHeader: false,
      active: "surface",
      networks: {
        surface: {
          name: "Surface",
          fileType: "gsn"
        },
        upperAir: {
          name: "Upper air",
          fileType: "guan"
        }
      }
    },
    snow_depth: {
      hasHeader: true,
      active: "snow_depth",
      networks: {
        snow_depth: {
          name: "Snow Depth",
          fileType: "synop"
        }
      }
    }
  });
  // The index of the month currently displayed
  // for each module/network
  let [currentIndexes, setCurrentIndexes] = useState({
    nwp: {
      synop: 0,
      temp: 0
    },
    gbon: {
      synop: 0,
      temp: 0
    },
    marine: {
      buoy: 0,
      ship: 0
    },
    gcos: {
      gsn: 0,
      guan: 0
    },
    snow_depth: {
      synop: 0
    }
  });
  // Contains an array of all the available months
  // per module/network
  let [possiblePeriods, setPossiblePeriods] = useState({
    nwp: {
      synop: null,
      temp: null
    },
    gbon: {
      synop: null,
      temp: null
    },
    marine: {
      buoy: null,
      ship: null
    },
    gcos: {
      gsn: null,
      guan: null
    },
    snow_depth: {
      synop: null
    }
  });
  let [body, setBody] = useState(
    <Spin className="spinner-periods" tip="Loading..." />
  );
  let [footer, setFooter] = useState(null);
  let [oscar_status, setOscarStatus] = useState(null);

  // When switching to the DB status page of another module
  useEffect(() => {
    setCurrentFileType(tabs[module].networks[tabs[module].active].fileType);

    // Google Analytics
    if (window.location.hostname !== "localhost") {
      initGA();
      logPageView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module]);

  // when changing module/tab
  useEffect(() => {
    // reset
    setBody(<Spin className="spinner-periods" tip="Loading..." />);
    setFooter(null);
    setOscarStatus(null);

    // check if the data was already fetched
    const networkCode = tabs[module].networks[tabs[module].active].fileType;
    let isAlreadyFetched = props[networkCode].dataFetched;
    if (module === "gbon") {
      isAlreadyFetched = props["gbon_" + networkCode].dataFetched;
    }
    if (!isAlreadyFetched) {
      if (module === "nwp") {
        if (networkCode === "synop") {
          props.onFetchSynopDataFromApi({
            fileType: networkCode,
            alreadyFetched: isAlreadyFetched,
            params: {}
          });
        } else if (networkCode === "temp") {
          props.onFetchTempDataFromApi({
            fileType: networkCode,
            alreadyFetched: isAlreadyFetched,
            params: {}
          });
        } else {
          console.error(`Unknown network code: ${networkCode}`);
        }
      } else if (module === "marine") {
        props.onFetchMarineDataFromApi({
          fileType: networkCode,
          alreadyFetched: isAlreadyFetched,
          params: {}
        });
      } else if (module === "gbon") {
        props.onFetchGbonDataFromApi({
          baseline: "GBON",
          fileType: "gbon_" + networkCode,
          parentFileType: networkCode, // either synop or temp
          alreadyFetched: isAlreadyFetched,
          params: {}
        });
      } else if (module === "snow_depth") {
        props.onFetchSynopDataFromApi({
          fileType: "synop",
          alreadyFetched: isAlreadyFetched,
          params: {}
        });
      } else if (module === "gcos") {
        props.onFetchGCOSDataFromApi({
          fileType: networkCode,
          alreadyFetched: isAlreadyFetched,
          params: {}
        });
      } else {
        props.onFetchGcosDataFromApi({
          fileType: networkCode,
          alreadyFetched: isAlreadyFetched,
          params: {}
        });
      }
    }
  }, [module, tabs]);

  // Done once per file type once the data has been fetched
  useEffect(() => {
    // If the data has been fetched

    if (module === "snow_depth") {
      // Use sdepPeriods which loads asynchronously after synop data
      const newState = { ...possiblePeriods };
      const sdepDates = props.synop?.sdepPeriods?.dates;
      if (sdepDates && sdepDates.all) {
        const allDates = Object.keys(sdepDates.all);
        if (allDates.length > 0) {
          const allDatesSorted = reverse(sortBy(allDates));
          newState["snow_depth"]["synop"] = getListMonths(
            allDatesSorted[0],
            allDatesSorted[allDatesSorted.length - 1]
          );
        } else {
          newState["snow_depth"]["synop"] = [];
        }
      } else if (props.synop?.dataFetched) {
        newState["snow_depth"]["synop"] = [];
      }
      setPossiblePeriods(newState);
    } else if (props[prefix + currentFileType]?.dataFetched) {
      const newState = { ...possiblePeriods };
      // If there is data for that module
      if (!props[prefix + currentFileType].noData) {
        let periods;
        if (module === "nwp" || module === "gbon" || module === "marine") {
          const originalData =
            props[prefix + currentFileType].periodData.six_hour.dates;

          if (originalData === null) {
            newState[module][currentFileType] = [];
            setPossiblePeriods(newState);
          } else {
            // Get all the dates from property 'all' in an array
            const allDates = Object.keys(originalData.all);

            // IMPORTANT: order dates from newest to oldest
            const allDatesSorted = reverse(sortBy(allDates));

            // For each month available get its last day
            periods = getListMonths(
              allDatesSorted[0],
              allDatesSorted[allDatesSorted.length - 1]
            );
          }
        } else {
          // GCOS
          // Get the last date of each year
          if (!props[currentFileType]?.dates) {
            newState[module][currentFileType] = [];
            setPossiblePeriods(newState);
            return;
          }
          const allDates = Object.keys(props[currentFileType].dates);
          periods = getListYears(allDates[0], allDates[allDates.length - 1]);
        }
        newState[module][currentFileType] = periods;
        setPossiblePeriods(newState);
      } else {
        // If there is no data for that module
        // set `possiblePeriods` for that module
        // to an empty array
        newState[module][currentFileType] = [];
        setPossiblePeriods(newState);
      }
    }
  }, [
    module,
    tabs,
    props[prefix + currentFileType]?.dataFetched,
    props[prefix + currentFileType]?.allDataFetched,
    props.synop?.sdepPeriods
  ]);

  // Done when clicking on previous or next month
  useEffect(() => {
    const periods = possiblePeriods[module][currentFileType];
    if (periods && periods.length > 0) {
      let allDates;
      let templateArray;
      if (module === "snow_depth") {
        allDates = props.synop?.sdepPeriods?.dates;
        templateArray = processNWPData(
          allDates,
          periods[currentIndexes[module][currentFileType]],
          ["DWD", "ECMWF"]
        );
      } else if (module === "nwp" || module === "gbon" || module === "marine") {
        allDates = props[prefix + currentFileType].periodData.six_hour.dates;
        templateArray = processNWPData(
          allDates,
          periods[currentIndexes[module][currentFileType]]
        );
        console.log(templateArray);
      } else {
        // GCOS
        allDates = Object.keys(props[currentFileType].dates);
        templateArray = processGCOSData(
          allDates,
          periods[currentIndexes[module][currentFileType]]
        );
      }

      setBody(
        <DBBody
          module={module}
          template={templateArray}
          fileType={currentFileType}
        />
      );
      setFooter(
        <DBFooter
          metadata={{
            module,
            currentFileType,
            currentIndexes
          }}
          periods={periods}
          index={currentIndexes[module][currentFileType]}
          onUpdateIndex={setCurrentIndexes}
        />
      );
      setOscarStatus(<DBOscarInfo />);
    } else if (periods && periods.length === 0) {
      // if `periods` is an empty array, it means that there is
      // no data for that module
      setBody(<div className="dbs-nodata">No data available</div>);
    }
  }, [
    prefix + currentFileType,
    currentIndexes,
    possiblePeriods[module][currentFileType]
  ]);

  const activateTab = tab => {
    // tab is an array. Index 0 is the key of one
    // of the 'tabs' we clicked on
    // Index 1 is its values

    // We only update the state if the tab is not active
    if (!tab[1].active) {
      const newState = { ...tabs };
      newState[module].active = tab[0];
      setTabs(newState);
      setCurrentFileType(newState[module].networks[tab[0]].fileType);
    }
  };

  const Tabs = Object.entries(tabs[module].networks).map(t => {
    const isActive = t[0] === tabs[module].active ? "active" : "inactive";
    return (
      <button
        key={t[0]}
        onClick={() => activateTab(t)}
        className={`tablink tablink-${isActive}`}
      >
        {t[1].name}
      </button>
    );
  });

  const header = tabs[module].hasHeader ? (
    <DBHeader centers={module === "snow_depth" ? ["DWD", "ECMWF"] : undefined} />
  ) : null;

  return (
    <div className="container-dbs">
      <div className="dbs-tabs">{Tabs}</div>
      <div className="dbs-header">{header}</div>
      <div className="dbs-body">{body}</div>
      <div className="dbs-footer">{footer}</div>
      <div className="oscar-status">{oscar_status}</div>
    </div>
  );
};

const mapStateToProps = state => {
  return {
    synop: state.synop,
    temp: state.temp,
    gbon_synop: state.gbon_synop,
    gbon_temp: state.gbon_temp,
    gsn: state.gsn,
    guan: state.guan,
    buoy: state.buoy,
    ship: state.ship
  };
};
const mapDispatchToProps = dispatch => {
  return {
    // onFetchDataFromApi: payload => {
    //   dispatch(actions.fetchDataFromApi(payload, "DB_STATUS"));
    // },
    onFetchSynopDataFromApi: payload => {
      dispatch(synopActions.fetchSynopDataFromApi(payload, "DB_STATUS"));
    },
    onFetchTempDataFromApi: payload => {
      dispatch(tempActions.fetchTempDataFromApi(payload, "DB_STATUS"));
    },
    onFetchMarineDataFromApi: payload => {
      dispatch(marineActions.fetchMarineDataFromApi(payload, "DB_STATUS"));
    },
    onFetchGbonDataFromApi: payload => {
      dispatch(gbonActions.fetchGbonDataFromApi(payload, "DB_STATUS"));
    },
    onFetchGcosDataFromApi: payload => {
      dispatch(gcosActions.fetchGcosDataFromApi(payload, "DB_STATUS"));
    },
    onFetchGCOSDataFromApi: payload => {
      dispatch(actions.fetchGCOSDataFromApi(payload));
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DatabaseStatus);
